import { useState, useEffect } from 'react';
import { Box, Button, TextField, Stack, Typography, IconButton, Paper, Snackbar } from '@mui/material';
import { Add as AddIcon, PlayArrow, Pause, Refresh, Delete, Alarm } from '@mui/icons-material';

export default function MultiTimer() {
  const [timers, setTimers] = useState(() => {
    const savedTimers = localStorage.getItem('multiTimers');
    return savedTimers ? JSON.parse(savedTimers) : [];
  });
  const [notificationPermission, setNotificationPermission] = useState(Notification.permission);

  const [workers, setWorkers] = useState({});

  useEffect(() => {
    localStorage.setItem('multiTimers', JSON.stringify(timers));
  }, [timers]);

  useEffect(() => {
    const loadTimers = () => {
      const savedTimers = localStorage.getItem('multiTimers');
      if (savedTimers) {
        const parsedTimers = JSON.parse(savedTimers);
        setTimers(parsedTimers);
        
        parsedTimers.forEach(timer => {
          if (timer.isRunning) {
            startWorkerForTimer(timer.id);
          }
        });
      }
    };

    loadTimers();
    
    return () => {
      Object.values(workers).forEach(worker => worker.terminate());
    };
  }, []);

  const startWorkerForTimer = (timerId) => {
    if (workers[timerId]) {
      workers[timerId].terminate();
    }

    const worker = new Worker(new URL('../../workers/timerWorker.js', import.meta.url));
    worker.onmessage = (e) => {
      if (e.data.type === 'tick') {
        setTimers(prevTimers => prevTimers.map(t => {
          if (t.id === timerId && t.isRunning) {
            return handleTimerTick(t);
          }
          return t;
        }));
      }
    };

    setWorkers(prev => ({ ...prev, [timerId]: worker }));
    worker.postMessage({ type: 'start' });
  };

  const handleTimerTick = (timer) => {
    if (timer.remaining <= 1) {
      clearInterval(timer.intervalId);
      showNotification(timer.label || 'Timer');
      return { ...timer, remaining: 0, isRunning: false, intervalId: null };
    }
    return { ...timer, remaining: timer.remaining - 1 };
  };

  const showNotification = (timerLabel) => {
    if (notificationPermission === "granted") {
      const notification = new Notification("Timer Complete!", {
        body: `${timerLabel} has finished!\nClick to snooze for 5 minutes`,
        icon: "/favicon.svg",
        requireInteraction: true,
        silent: false
      });

      notification.onclick = () => {
        setTimers(prevTimers => prevTimers.map(t => 
          t.label === timerLabel 
            ? { ...t, remaining: 300, isRunning: true }
            : t
        ));
        notification.close();
      };
    }
  };

  const addTimer = () => {
    setTimers([...timers, { 
      id: Date.now(),
      duration: 0,
      remaining: 0,
      isRunning: false,
      intervalId: null,
      label: ''
    }]);
  };

  const startTimer = (id) => {
    setTimers(timers.map(timer => {
      if (timer.id === id && !timer.isRunning && timer.duration > 0) {
        const intervalId = setInterval(() => {
          setTimers(prevTimers => prevTimers.map(t => {
            if (t.id === id && t.isRunning) {
              return handleTimerTick(t);
            }
            return t;
          }));
        }, 1000);
        return { ...timer, isRunning: true, intervalId: intervalId, remaining: timer.duration };
      }
      return timer;
    }));
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return { hrs, mins, secs };
  };

  useEffect(() => {
    if (Notification.permission !== "granted") {
      setNotificationPermission(Notification.permission)
    }
  }, []);


  const requestNotificationPermission = () => {
    Notification.requestPermission().then(permission => {
      setNotificationPermission(permission);
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4">Multi-Timer</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={addTimer}
        >
          Add Timer
        </Button>
      </Stack>

      {notificationPermission !== "granted" && (
        <Button
          variant="outlined"
          startIcon={<Alarm />}
          onClick={requestNotificationPermission}
          sx={{ mb: 2 }}
        >
          Enable Notifications
        </Button>
      )}

      <Stack spacing={3}>
        {timers.map(timer => {
          const { hrs, mins, secs } = formatTime(timer.remaining);
          return (
            <Paper
              key={timer.id}
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: 3,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                }
              }}
            >
              <Stack spacing={2}>
                <TextField
                  label="Timer Label"
                  value={timer.label}
                  onChange={(e) => setTimers(timers.map(t => 
                    t.id === timer.id ? { ...t, label: e.target.value } : t
                  ))}
                  sx={{ width: 200 }}
                />
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    type="number"
                    label="Hours"
                    value={Math.floor(timer.duration / 3600)}
                    onChange={(e) => {
                      const newHours = Math.max(0, parseInt(e.target.value) || 0);
                      const newDuration = (newHours * 3600) + (Math.floor(timer.duration % 3600));
                      setTimers(timers.map(t => 
                        t.id === timer.id ? { ...t, duration: newDuration } : t
                      ));
                    }}
                    disabled={timer.isRunning}
                    sx={{ width: 100 }}
                  />
                  <TextField
                    type="number"
                    label="Minutes"
                    value={Math.floor((timer.duration % 3600) / 60)}
                    onChange={(e) => {
                      const newMinutes = Math.max(0, Math.min(59, parseInt(e.target.value) || 0));
                      const hours = Math.floor(timer.duration / 3600);
                      const newDuration = (hours * 3600) + (newMinutes * 60) + (timer.duration % 60);
                      setTimers(timers.map(t => 
                        t.id === timer.id ? { ...t, duration: newDuration } : t
                      ));
                    }}
                    disabled={timer.isRunning}
                    sx={{ width: 100 }}
                  />
                  <TextField
                    type="number"
                    label="Seconds"
                    value={timer.duration % 60}
                    onChange={(e) => {
                      const newSeconds = Math.max(0, Math.min(59, parseInt(e.target.value) || 0));
                      const minutes = Math.floor((timer.duration % 3600) / 60);
                      const hours = Math.floor(timer.duration / 3600);
                      const newDuration = (hours * 3600) + (minutes * 60) + newSeconds;
                      setTimers(timers.map(t => 
                        t.id === timer.id ? { ...t, duration: newDuration } : t
                      ));
                    }}
                    disabled={timer.isRunning}
                    sx={{ width: 100 }}
                  />
                </Stack>

                <div className="grid grid-flow-col gap-5 text-center auto-cols-max">
                  <div className="flex flex-col p-2 bg-neutral rounded-box text-neutral-content">
                    <span className="countdown font-mono text-4xl">
                      <span style={{ "--value": hrs }}></span>
                    </span>
                    hours
                  </div>
                  <div className="flex flex-col p-2 bg-neutral rounded-box text-neutral-content">
                    <span className="countdown font-mono text-4xl">
                      <span style={{ "--value": mins }}></span>
                    </span>
                    min
                  </div>
                  <div className="flex flex-col p-2 bg-neutral rounded-box text-neutral-content">
                    <span className="countdown font-mono text-4xl">
                      <span style={{ "--value": secs }}></span>
                    </span>
                    sec
                  </div>
                </div>

                <Stack direction="row" spacing={1} justifyContent="center">
                  {!timer.isRunning ? (
                    <IconButton onClick={() => startTimer(timer.id)} color="primary" size="large">
                      <PlayArrow />
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => {
                      clearInterval(timer.intervalId);
                      setTimers(timers.map(t => 
                        t.id === timer.id ? { ...t, isRunning: false, intervalId: null } : t
                      ));
                    }} color="warning" size="large">
                      <Pause />
                    </IconButton>
                  )}
                  <IconButton onClick={() => {
                    if (timer.isRunning) {
                      clearInterval(timer.intervalId);
                    }
                    setTimers(timers.map(t => 
                      t.id === timer.id ? { ...t, remaining: timer.duration, isRunning: false, intervalId: null } : t
                    ));
                  }} color="info" size="large">
                    <Refresh />
                  </IconButton>
                  <IconButton onClick={() => {
                    if (timer.isRunning) {
                      clearInterval(timer.intervalId);
                    }
                    setTimers(timers.filter(t => t.id !== timer.id));
                  }} color="error" size="large">
                    <Delete />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}